import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router";
import axios from "axios";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

import AllDrugKits from "../Drug/AllDrugKits";
import BasicInfoAccordion from "../../components/TrialAccordions/BasicInfoAccordion";
import AddNewPatientModal from "../../components/Modals/AddNewPatient";
import VisitTabAccordion from "../../components/TrialAccordions/VisitTabAccordion";
import TrialNotificationAccordion from "../../components/TrialAccordions/TrialNotificationAccordion";
import SurveyHistogramAccordion from "../../components/TrialAccordions/SurveyHistogramAccordion";
import SurveyPoem from "../../components/Survey/SurveyPoem";
import SessionActivitiesAccordion from "../../components/TrialAccordions/SessionActivitiesAccordion";
import { __API_URL__ } from "../../SERVER_URL";
import { LoaderContext } from "../../context/LoaderContext";
import useGetVisits from "../../hooks/useGetVisits";
import useFinalizeTrialConfigMutation from "../../hooks/useFinalizeTrialConfigMutation";
import useUnfinalizeTrialConfigMutation from "../../hooks/useUnfinalizeTrialConfigMutation";
import { useEcrfContext } from "../../context/EcrfContext";
import { isTrialConfigFinalized, isTrialPendingFinalization } from "../../utils/helpers";

const TrialDetails = () => {
  const token = localStorage.getItem("token");
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;

  const loaderContext = useContext(LoaderContext);

  const {
    selectedVisit,
    setSelectedVisit,
    eventList,
    setEventList,
    setAllVisits,
    initialFlag,
    setInitialFlag,
    setZeroVisit,
    setTrial,
    trial,
    setHasChanged,
  } = useEcrfContext();

  const isTrialLocked = ["Completed", "Archived"].includes(trial?.status);
  // Finalization only applies to Live trials; a UAT trial is neither finalized nor pending
  const isFinalized = isTrialConfigFinalized(trial);
  const isPendingFinalization = isTrialPendingFinalization(trial);
  const canFinalize = role === "CRO Admin" || role === "Super Admin";
  const canUnfinalize = role === "Super Admin";

  const finalizeMutation = useFinalizeTrialConfigMutation();
  const unfinalizeMutation = useUnfinalizeTrialConfigMutation();
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);

  const { _id_ } = useParams();

  const handleFinalizeToggle = async () => {
    try {
      const mutation = isFinalized ? unfinalizeMutation : finalizeMutation;
      const result = await mutation.mutateAsync({ trialId: _id_ });
      setTrial((prev) => ({ ...prev, is_finalized: result.is_finalized }));
      setFinalizeDialogOpen(false);
      toast.success(result.status);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not update the trial configuration state.");
      setFinalizeDialogOpen(false);
    }
  };

  const [typeOfMessage, setTypeOfMessage] = useState("");
  const [message, setMessage] = useState("");
  const [allSurveys, setAllSurveys] = useState([]);
  const [selectedTrialType, setSelectedTrialType] = useState("");
  const [modalShow, setModalShow] = useState(false);
  const [modalPatientShow, setModalPatientShow] = useState(false);
  const [modalVisitChangeShow, setModalVisitChangeShow] = useState(false);
  const [numberOfVisitsArray, setNumberOfVisitsArray] = useState([]);
  const [surveyFormBool, setSurveyFormBool] = useState(false);
  const [value, setValue] = useState(0);
  const [consentFormBool, setConsentFormBool] = useState(false);
  const [isEvent, setisEvent] = useState({
    adverse_event: false,
    unscheduled_event: false,
    screening_visit: false,
    ediary: false,
  });
  const [addNewSurvey, setAddNewSurvey] = useState(false);
  const [viewDrug, setViewDrug] = useState(false);

  const handleVisitFieldChange = (e) => {
    const { name, value } = e.target;

    if (name === "app_videos") {
      setSelectedVisit((visit) => {
        return {
          ...visit,
          [name]: !visit.app_videos,
        };
      });
    } else if (["Lesion 1", "Lesion 2", "Lesion 3"].includes(name)) {
      setSelectedVisit((visit) => {
        let current_extras = visit.extra_photos;
        const index = current_extras.indexOf(name);
        if (index > -1) current_extras.splice(index, 1);
        else current_extras.push(name);
        return {
          ...visit,
          extra_photos: current_extras,
        };
      });
    } else if (name === "title" || name === "content") {
      setSelectedVisit((visit) => {
        return {
          ...visit,
          consent_form: {
            ...visit.consent_form,
            [name]: value,
          },
        };
      });
    } else if (name === "consent_list_id") {
      setSelectedVisit((visit) => {
        return {
          ...visit,
          consent_form: {
            ...visit.consent_form,
            id: value,
          },
        };
      });
    } else {
      setSelectedVisit((visit) => {
        return {
          ...visit,
          [name]: value,
        };
      });
    }
  };

  const AllSurveyData = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);
    axios
      .get(`${__API_URL__}/api/survey/survey_trial`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setAllSurveys(res.data);
      })
      .catch((err) => {
        console.error("Error: ", err);
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const { data: visits, refetch } = useGetVisits({
    trialId: _id_,
    enabled: !!_id_,
  });

  useEffect(() => {
    if (!!visits) {
      setAllVisits(visits);
      initialDataProcessing(visits);
    }
  }, [refetch, visits]);

  const initialDataProcessing = (data) => {
    const screeningVisitSelected = data.filter((visit) => {
      return visit.visit_number === -3;
    });

    const zeroVisitSelected = data.filter((visit) => {
      return visit.visit_number === 0;
    });

    setZeroVisit(zeroVisitSelected[0]);

    if (initialFlag) {
      if (data.length > 0) {
        let filteredData = data.filter((item) => [-1, -2, -3, -4, -6].includes(item.visit_number));
        let visitNumbers = filteredData.map((item) => item.visit_number);
        setEventList(visitNumbers);

        if (screeningVisitSelected.length > 0) {
          setSelectedVisit({
            ...screeningVisitSelected[0],
            start_date: new Date(screeningVisitSelected[0].start_date + "Z"),
          });
        } else if (zeroVisitSelected.length > 0) {
          setSelectedVisit({
            ...zeroVisitSelected[0],
            start_date: new Date(zeroVisitSelected[0].start_date + "Z"),
          });
        }
      } else {
        setHasChanged(true);
      }
    } else {
      // A saved visit is matched by id because its number can change when visits are renumbered
      const concernedVisitSelected = data.filter((visit) => {
        return selectedVisit?.id !== undefined
          ? visit.id === selectedVisit.id
          : visit.visit_number === selectedVisit?.visit_number;
      });
      if (concernedVisitSelected.length > 0) {
        setSelectedVisit({
          ...concernedVisitSelected[0],
          start_date: new Date(concernedVisitSelected[0].start_date + "Z"),
        });
      }
    }

    setInitialFlag(false);
  };

  const trialDetailApi = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);
    axios
      .get(`${__API_URL__}/api/photos/get_trial/${_id_}/`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setTrial(res?.data);
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const handleSelectingVisit = (zeroVisit) => {
    if (zeroVisit.consent_form.id) {
      setConsentFormBool(true);
    } else setConsentFormBool(false);
    if (zeroVisit.survey_form.id) {
      setSurveyFormBool(true);
    } else setSurveyFormBool(false);
  };

  useEffect(() => {
    trialDetailApi();
    AllSurveyData();
  }, []);

  useEffect(() => {
    if (eventList?.includes(-1)) {
      setisEvent((prevState) => ({
        ...prevState,
        adverse_event: true,
      }));
    }
    if (eventList?.includes(-2)) {
      setisEvent((prevState) => ({
        ...prevState,
        unscheduled_event: true,
      }));
    }
    if (eventList?.includes(-3)) {
      setisEvent((prevState) => ({
        ...prevState,
        screening_visit: true,
      }));
    }
  }, [eventList]);

  return (
    <Box>
      {viewDrug ? (
        <AllDrugKits
          setMessage={setMessage}
          setTypeOfMessage={setTypeOfMessage}
          trial={_id_}
          manual_drug={trial?.manual_drug}
          unassign_drug_count={trial?.unassign_drug_count}
          trialDetailApi={trialDetailApi}
        />
      ) : (
        <Box sx={{ my: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              {trial?.trial_structure !== "session_based" &&
                (role === "Investigator" ||
                  role === "Sub_investigator" ||
                  role === "Inv_coordinator") && (
                  <Button
                    variant="contained"
                    size="small"
                    data-testid="add-subject-btn"
                    disabled={isTrialLocked || isPendingFinalization}
                    title={
                      isPendingFinalization
                        ? "Subjects can be added once the trial configuration is finalized"
                        : undefined
                    }
                    onClick={() => setModalPatientShow(true)}
                  >
                    Add Subject
                  </Button>
                )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {trial?.id !== undefined &&
                !isTrialLocked &&
                isPendingFinalization &&
                canFinalize && (
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    data-testid="finalize-trial-config-btn"
                    onClick={() => setFinalizeDialogOpen(true)}
                  >
                    Finalize Trial Configuration
                  </Button>
                )}
              {trial?.id !== undefined && isFinalized && canUnfinalize && (
                <Button
                  variant="outlined"
                  size="small"
                  data-testid="unfinalize-trial-config-btn"
                  onClick={() => setFinalizeDialogOpen(true)}
                >
                  Un-finalize Configuration
                </Button>
              )}
            </Stack>
          </Stack>

          {trial?.id !== undefined && isPendingFinalization && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This Live trial is in setup. Only CRO and Super Admin users can see it, and subjects
              cannot be added until the configuration is finalized. Visit numbers follow the visit
              schedule: if two visits overlap, only the first one's app activities show until it is
              completed.
            </Alert>
          )}

          <BasicInfoAccordion
            setMessage={setMessage}
            setTypeOfMessage={setTypeOfMessage}
            isEvent={isEvent}
            setisEvent={setisEvent}
            setSelectedTrialType={setSelectedTrialType}
          />

          {trial?.trial_structure === "session_based" ? (
            <>
              <SessionActivitiesAccordion trialId={_id_} role={role} />
              {trial?.show_survey_histogram && (
                <SurveyHistogramAccordion
                  trialId={_id_}
                  setMessage={setMessage}
                  setTypeOfMessage={setTypeOfMessage}
                />
              )}
            </>
          ) : (
            <>
              <VisitTabAccordion
                isEvent={isEvent}
                value={value}
                setValue={setValue}
                numberOfVisitsArray={numberOfVisitsArray}
                setNumberOfVisitsArray={setNumberOfVisitsArray}
                surveyFormBool={surveyFormBool}
                setSurveyFormBool={setSurveyFormBool}
                consentFormBool={consentFormBool}
                setConsentFormBool={setConsentFormBool}
                handleSelectingVisit={handleSelectingVisit}
                setMessage={setMessage}
                setTypeOfMessage={setTypeOfMessage}
                setModalVisitChangeShow={setModalVisitChangeShow}
                setAddNewSurvey={setAddNewSurvey}
                allSurveys={allSurveys}
                selectedTrialType={selectedTrialType}
              />
              {trial?.show_survey_histogram && (
                <SurveyHistogramAccordion
                  trialId={_id_}
                  setMessage={setMessage}
                  setTypeOfMessage={setTypeOfMessage}
                />
              )}
            </>
          )}
          <TrialNotificationAccordion
            trialId={_id_}
            trialStructure={trial?.trial_structure}
            setMessage={setMessage}
            setTypeOfMessage={setTypeOfMessage}
          />
        </Box>
      )}

      {/* Add/Update Consent Dialog */}
      <Dialog open={modalShow} onClose={() => setModalShow(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add/Update Consent
          <IconButton
            aria-label="close"
            onClick={() => setModalShow(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Title"
            size="small"
            name="title"
            value={selectedVisit?.consent_form?.title || ""}
            onChange={(e) => handleVisitFieldChange(e)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Content"
            size="small"
            name="content"
            value={selectedVisit?.consent_form?.content || ""}
            onChange={(e) => handleVisitFieldChange(e)}
            multiline
            minRows={3}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={finalizeDialogOpen}
        onClose={() => setFinalizeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isFinalized ? "Un-finalize Trial Configuration" : "Finalize Trial Configuration"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ py: 1 }}>
            {isFinalized
              ? "The trial configuration will become editable again and the trial will be hidden from non-CRO users. This is only possible while no subjects have been added."
              : "Once finalized, the trial becomes visible to investigators and other users, subjects can be added, and the visit configuration becomes read-only. Only items like investigator assignment can still be changed."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setFinalizeDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            data-testid="confirm-finalize-trial-config-btn"
            onClick={handleFinalizeToggle}
            disabled={finalizeMutation.isPending || unfinalizeMutation.isPending}
          >
            {isFinalized ? "Un-finalize" : "Finalize"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Patient Modal */}
      {modalPatientShow && <AddNewPatientModal modalShow={setModalPatientShow} trial_id={_id_} />}

      {/* Unsaved Changes Dialog */}
      <Dialog
        open={modalVisitChangeShow}
        onClose={() => setModalVisitChangeShow(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Unsaved Changes
          <IconButton
            aria-label="close"
            onClick={() => setModalVisitChangeShow(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography align="center" sx={{ py: 1 }}>
            You have Unsaved changes for the current opened Visit!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              setHasChanged(false);
              setModalVisitChangeShow(false);
            }}
          >
            Discard Changes
          </Button>
          <Button variant="contained" onClick={() => setModalVisitChangeShow(false)}>
            Continue Making Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Survey Dialog */}
      <Dialog
        open={addNewSurvey}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") setAddNewSurvey(false);
        }}
        disableEscapeKeyDown
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={() => setAddNewSurvey(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <SurveyPoem
            setAddNewSurvey={setAddNewSurvey}
            AllSurveyData={AllSurveyData}
            trial_detail={true}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TrialDetails;
